import React, { createContext, useContext, useState, useCallback, Suspense } from 'react';

// BugReportModal statically imports html2canvas + html-to-image (~55KB gz combined).
// Lazy-load it so the capture libs stay out of the main bundle every visitor
// downloads (same playbook as RichText/LazyMath for react-katex) — the chunk
// only downloads on the first openBugReport().
const BugReportModal = React.lazy(() => import('../components/BugReportModal'));

const BugReportContext = createContext(null);

/**
 * Provides active question context to the app-wide bug report modal.
 * Any question-displaying component (LevelSession, QuizSession, BossSession)
 * can call setActiveQuestion() so that when the user opens the bug report,
 * the question content is automatically attached.
 *
 * Shape of questionContext:
 * {
 *   question_text: string,
 *   block_type: string,          // "mcq", "fill_blank", "true_false", "frq", etc.
 *   options: object|array|null,  // MCQ options
 *   correct_answer: string,
 *   user_answer: string|null,
 *   was_correct: bool|null,
 *   explanation: string|null,
 *   deck_id: string|null,
 *   deck_title: string|null,
 *   subtopic_id: string|null,
 *   subtopic_name: string|null,
 *   block_index: number|null,
 *   source_slides: array|null,
 * }
 */
export function BugReportProvider({ children }) {
  const [activeQuestion, setActiveQuestionRaw] = useState(null);
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const [capturedQuestion, setCapturedQuestion] = useState(null);

  const setActiveQuestion = useCallback((ctx) => {
    setActiveQuestionRaw(ctx);
  }, []);

  const clearActiveQuestion = useCallback(() => {
    setActiveQuestionRaw(null);
  }, []);

  const openBugReport = useCallback((questionContext = activeQuestion) => {
    setCapturedQuestion(questionContext || null);
    setBugModalOpen(true);
  }, [activeQuestion]);

  const closeBugReport = useCallback(() => {
    setBugModalOpen(false);
    setCapturedQuestion(null);
  }, []);

  return (
    <BugReportContext.Provider
      value={{
        activeQuestion,
        setActiveQuestion,
        clearActiveQuestion,
        openBugReport,
        closeBugReport,
      }}
    >
      {children}
      {bugModalOpen && (
        <Suspense fallback={null}>
          <BugReportModal
            isOpen={bugModalOpen}
            onClose={closeBugReport}
            questionContext={capturedQuestion}
          />
        </Suspense>
      )}
    </BugReportContext.Provider>
  );
}

export function useBugReport() {
  const ctx = useContext(BugReportContext);
  if (!ctx) throw new Error('useBugReport must be used within BugReportProvider');
  return ctx;
}
